import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Plane, Briefcase, Route, UserSquare2, HeartHandshake, Building2, ArrowUpRight } from "lucide-react";
import { BOOKING_URL } from "@/data/site";

const SLIDES = [
  {
    key: "gatwick",
    title: "Luxury Gatwick transfers",
    kicker: "Airport",
    desc: "A DBS-checked chauffeur waiting airside with your name in hand. Flight-tracked, luggage-loaded, quietly perfect.",
    image: "/images/services/airport.jpg",
    duration: "10 min from Crawley",
    price: "From £25",
    Icon: Plane,
  },
  {
    key: "business",
    title: "Executive business travel",
    kicker: "Corporate",
    desc: "Roadshows, board meetings, discreet transfers. Mercedes E-Class and S-Class, on-time to the minute.",
    image: "/images/services/corporate.jpg",
    duration: "Door to door, London in 60 min",
    price: "From £75",
    Icon: Briefcase,
  },
  {
    key: "distance",
    title: "Long-distance chauffeur",
    kicker: "Journey",
    desc: "Cinematic drives across the Surrey Hills to London, the coast or beyond. Fixed fares, no meter, no hurry.",
    image: "/images/hero/clocktower-car.jpg",
    duration: "Nationwide, all hours",
    price: "Quoted per route",
    Icon: Route,
  },
  {
    key: "meet",
    title: "Meet and Greet",
    kicker: "Arrivals",
    desc: "Your chauffeur meets you inside the terminal with a personalised name board, then walks you calmly to the car.",
    image: "/images/services/chauffeur.jpg",
    duration: "45 min free wait from touchdown",
    price: "Included",
    Icon: UserSquare2,
  },
  {
    key: "wedding",
    title: "Weddings and special events",
    kicker: "Occasions",
    desc: "An immaculate black Mercedes waiting at the door of your venue. Discreet, on time, dressed for the moment.",
    image: "/images/services/event.jpg",
    duration: "Fixed hourly hire",
    price: "On request",
    Icon: HeartHandshake,
  },
  {
    key: "accounts",
    title: "Corporate accounts",
    kicker: "For teams",
    desc: "One point of contact, monthly invoicing, VAT receipts and executive vehicles for the people who move your business.",
    image: "/images/services/corporate.jpg",
    duration: "24/7, single dispatcher",
    price: "Custom pricing",
    Icon: Building2,
  },
];

export const StoryShowcase = () => {
  const sectionRef = useRef(null);
  const trackRef = useRef(null);

  const x = useMotionValue(0);
  const smoothX = useSpring(x, { stiffness: 55, damping: 22, mass: 0.9 });
  const p = useMotionValue(0);
  const smoothP = useSpring(p, { stiffness: 60, damping: 20 });
  const progressWidth = useTransform(smoothP, (v) => `${Math.max(0.04, Math.min(v, 1)) * 100}%`);

  const handleMove = (e) => {
    if (typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches) return;
    const section = sectionRef.current;
    const track = trackRef.current;
    if (!section || !track) return;
    const rect = section.getBoundingClientRect();
    const px = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    const maxOffset = Math.max(0, track.scrollWidth - rect.width);
    x.set(-px * maxOffset);
    p.set(px);
  };

  return (
    <section
      ref={sectionRef}
      onMouseMove={handleMove}
      data-testid="story-showcase"
      className="relative overflow-hidden bg-ink py-24 md:py-32"
    >
      {/* ambient green glow */}
      <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 h-[420px] w-[900px] rounded-full bg-brand/15 blur-[140px]" />

      {/* header */}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 mb-14 md:mb-16">
        <p className="text-xs font-bold uppercase tracking-[0.28em] mb-5 text-brand-light">The showcase</p>
        <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-white tracking-tight leading-[1.05] max-w-4xl">
          Move your cursor. <span className="text-white/50">Let the journey unfold.</span>
        </h2>
        <p className="mt-6 text-white/50 max-w-xl leading-relaxed">
          A first-class chauffeur experience across Gatwick, Surrey and beyond. Slide sideways to explore.
        </p>
      </div>

      {/* horizontal track */}
      <div className="relative">
        <motion.div
          ref={trackRef}
          style={{ x: smoothX }}
          drag="x"
          dragConstraints={{ left: -3000, right: 0 }}
          dragElastic={0.05}
          className="flex gap-6 md:gap-8 pl-6 lg:pl-8 pr-24 md:pr-40 will-change-transform touch-pan-y select-none"
        >
          {SLIDES.map((s) => (
            <StoryCard key={s.key} slide={s} />
          ))}
        </motion.div>
      </div>

      {/* minimal progress line */}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 mt-16 md:mt-20">
        <div className="h-px w-full bg-white/10 overflow-hidden">
          <motion.div
            style={{ width: progressWidth }}
            className="h-full bg-brand-light"
            data-testid="story-progress"
          />
        </div>
        <div className="mt-4 flex items-center justify-between text-[10px] uppercase tracking-[0.3em] text-white/40 font-semibold">
          <span>Slide the cursor</span>
          <span className="hidden md:inline">Six chapters</span>
          <span>Or swipe</span>
        </div>
      </div>
    </section>
  );
};

const StoryCard = ({ slide }) => {
  const { title, kicker, desc, image, duration, price, Icon } = slide;
  return (
    <motion.article
      data-testid={`story-card-${slide.key}`}
      whileHover="hover"
      initial="rest"
      animate="rest"
      className="relative shrink-0 h-[520px] md:h-[600px] w-[85vw] md:w-[68vw] lg:w-[52vw] xl:w-[48vw] rounded-3xl overflow-hidden bg-neutral-900 shadow-[0_30px_90px_-30px_rgba(0,0,0,0.6)]"
    >
      <motion.img
        src={image}
        alt={title}
        draggable="false"
        loading="lazy"
        variants={{ rest: { scale: 1 }, hover: { scale: 1.08 } }}
        transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
        className="absolute inset-0 h-full w-full object-cover pointer-events-none"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/45 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink/60 via-transparent to-transparent" />

      {/* top row */}
      <div className="absolute top-7 left-7 right-7 flex items-center justify-between text-white/70">
        <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] font-semibold">
          <Icon className="h-4 w-4 text-brand-light" strokeWidth={1.75} /> {kicker}
        </span>
        <span className="text-[11px] tracking-[0.2em] uppercase font-medium">Clocktower Cars</span>
      </div>

      {/* bottom content */}
      <div className="absolute inset-x-0 bottom-0 p-8 md:p-10">
        <motion.h3
          variants={{ rest: { y: 0 }, hover: { y: -6 } }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="font-heading text-3xl md:text-4xl lg:text-[44px] font-light tracking-tight text-white leading-[1.05] max-w-[85%]"
        >
          {title}
        </motion.h3>
        <motion.p
          variants={{ rest: { opacity: 0.7, y: 4 }, hover: { opacity: 1, y: 0 } }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="mt-4 text-white/70 max-w-md leading-relaxed"
        >
          {desc}
        </motion.p>
        <div className="mt-7 flex flex-wrap items-center gap-x-8 gap-y-3 text-[11px] uppercase tracking-[0.22em] text-white/55 font-medium">
          <span>{duration}</span>
          {price && <span className="text-brand-light">{price}</span>}
        </div>
        <motion.div
          variants={{ rest: { opacity: 0.9 }, hover: { opacity: 1 } }}
          className="mt-8"
        >
          <a
            href={BOOKING_URL}
            data-testid={`story-book-${slide.key}`}
            className="group inline-flex items-center gap-3 border border-white/25 hover:border-brand-light text-white hover:text-brand-light px-7 py-3.5 rounded-full text-sm font-medium tracking-wide transition-colors duration-300"
          >
            Book now
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </motion.div>
      </div>
    </motion.article>
  );
};
